import {
  keepPreviousData,
  QueryObserverResult,
  UseQueryResult,
  useQueryClient,
  useInfiniteQuery as useTanstackInfiniteQuery,
  useMutation as useTanstackMutation,
  useQuery as useTanstackQuery,
} from '@tanstack/react-query'
import { AxiosError, AxiosInstance } from 'axios'
import { useEffect } from 'react'
import { useToast } from '@pubstack/ui/hooks/useToasts'
import { useAxios } from './AxiosContext'

type UseQueryParams<T = unknown, S = T> = {
  url: string
  keys: (string | object)[]
  enabled?: boolean
  keepPreviousDataEnabled?: boolean
  refetchInterval?: number | false | ((query: { state: { data: T | undefined } }) => number | false)
  /**
   * Reshapes the response before the caller sees it, e.g. `sortByName`. Pass a module-level function, never an
   * inline arrow: tanstack only reruns `select` when its reference or the data changes.
   */
  select?: (data: T) => S
}

type UseInfiniteQueryParams<T> = UseQueryParams & {
  initialPageParam: number
  getNextPageParam: (lastPage: T, allPages: T[], lastPageParam: number) => number | undefined
  buildUrl: (url: string, page: number) => string
}

type UseMutationParams = {
  method: 'post' | 'put' | 'patch' | 'delete'
  url: string
  collectionKeys: (string | object)[]
  /** Other collections the mutation changes server-side, invalidated alongside `collectionKeys`. */
  invalidatedKeys?: (string | object)[][]
  resourceId?: string
  isOptimistic?: boolean
  successMessage?: string
  errorMessage?: string
}

export const get = async <T>(api: AxiosInstance, url: string): Promise<T> => {
  const { data } = await api.get(url)
  return data
}

export const post = async <T>(api: AxiosInstance, url: string, body: any, signal: AbortSignal): Promise<T> => {
  const { data } = await api.post(url, body, { signal })
  return data
}

export const useQuery = <T, S = T>({ url, keys, enabled = true, keepPreviousDataEnabled = false, refetchInterval, select }: UseQueryParams<T, S>) => {
  const api = useAxios()
  const toast = useToast()
  const query = useTanstackQuery<T, Error, S>({
    queryKey: keys,
    queryFn: () => get<T>(api, url),
    enabled,
    placeholderData: keepPreviousDataEnabled ? keepPreviousData : undefined,
    refetchInterval,
    select,
  })

  useEffect(() => {
    if (query.error && !refetchInterval) {
      toast.alert('An error occurred while trying to fetch data.')
    }
  }, [query.error, refetchInterval])

  return query
}

export const useInfiniteQuery = <T>({ url, keys, enabled = true, initialPageParam, getNextPageParam, buildUrl }: UseInfiniteQueryParams<T>) => {
  const api = useAxios()
  const toast = useToast()
  const query = useTanstackInfiniteQuery({
    queryKey: keys,
    queryFn: async ({ pageParam: page }) => get<T>(api, buildUrl(url, page)),
    enabled,
    initialPageParam,
    getNextPageParam,
  })

  useEffect(() => {
    if (query.error) {
      toast.alert('An error occurred while trying to fetch data.')
    }
  }, [query.error])

  return query
}

export const useLazyQuery = <T>({ url, keys }: Pick<UseQueryParams, 'url' | 'keys'>) => {
  const query = useQuery<T>({ url, keys, enabled: false })

  return {
    ...query,
    fetch: query.refetch,
  } as UseQueryResult<T, Error> & { fetch: () => Promise<QueryObserverResult<T, Error>> }
}

export const useMutation = <TVariables = void, TData = unknown>({
  method,
  url,
  collectionKeys,
  invalidatedKeys = [],
  resourceId,
  isOptimistic = false,
  successMessage,
  errorMessage,
}: UseMutationParams) => {
  const api = useAxios()
  const queryClient = useQueryClient()
  const toast = useToast()
  return useTanstackMutation<TData, any, TVariables, { previousCollection: unknown; previousEntity: unknown }>({
    mutationFn: async (body: TVariables | undefined) => {
      try {
        const response = method === 'delete' ? await api[method](url) : await api[method](url, body)
        return response.data
      } catch (error) {
        if (error && typeof error === 'object' && 'response' in error) {
          throw (error as AxiosError).response?.data || error
        }
        throw error
      }
    },
    onMutate: async (data: TVariables) => {
      if (!isOptimistic) {
        return { previousCollection: undefined, previousEntity: undefined }
      }

      await queryClient.cancelQueries({ queryKey: collectionKeys })
      await queryClient.cancelQueries({ queryKey: [...collectionKeys, resourceId] })

      const previousCollection = queryClient.getQueryData(collectionKeys)
      const previousEntity = queryClient.getQueryData([...collectionKeys, resourceId])

      queryClient.setQueryData(collectionKeys, (old: TVariables[]) => [...(old || []).filter((e) => (e as any).id !== (data as any).id), data])
      queryClient.setQueryData([...collectionKeys, resourceId], data)

      return { previousCollection, previousEntity }
    },
    onError: (_e, _variables, context) => {
      if (isOptimistic) {
        context && queryClient.setQueryData(collectionKeys, context.previousCollection)
        context && queryClient.setQueryData([...collectionKeys, resourceId], context.previousEntity)
      }

      if (errorMessage) toast.alert(errorMessage)
    },
    onSuccess: () => {
      if (successMessage) toast.success(successMessage)

      if (!isOptimistic) queryClient.invalidateQueries({ queryKey: collectionKeys })
      for (const queryKey of invalidatedKeys) queryClient.invalidateQueries({ queryKey })
    },
    onSettled: () => {
      if (isOptimistic) queryClient.invalidateQueries({ queryKey: collectionKeys })
    },
  })
}
